import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Fuse from "fuse.js";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Search, Book, Building2, User, Loader2, X, Clock, Trash2, Layers, Library } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSearchHistory, SearchHistoryItem } from "@/hooks/useSearchHistory";

interface SearchResult {
  type: "book" | "publisher" | "author" | "book_type" | "series";
  id: string | number;
  title: string;
  subtitle?: string;
  slug: string;
  coverUrl?: string;
  logoUrl?: string;
  metadata?: string;
}

// Highlight matching text
const HighlightText = ({ text, query }: { text: string; query: string }) => {
  if (!query.trim()) return <>{text}</>;
  
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, "gi"));
  
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-primary/20 text-primary font-semibold rounded px-0.5">
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
};

interface SearchCommandProps {
  isMobile?: boolean;
}

const SearchCommand = ({ isMobile = false }: SearchCommandProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { history, addToHistory, removeFromHistory, clearHistory } = useSearchHistory();

  // Debounce search query (200ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard shortcut to focus search (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Fetch all searchable data
  const { data: books = [], isLoading: booksLoading } = useQuery({
    queryKey: ["search-books"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("books")
        .select("id, title, original_title, slug, cover_url, publisher_name, author_name, illustrator_name, book_type, book_series, isbn, age_group, description")
        .or("archived.is.null,archived.eq.false");
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: publishers = [], isLoading: publishersLoading } = useQuery({
    queryKey: ["search-publishers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("publishers")
        .select("id, name, slug, country")
        .or("archived.is.null,archived.eq.false");
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: authors = [], isLoading: authorsLoading } = useQuery({
    queryKey: ["search-authors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("authors")
        .select("id, name, slug");
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = booksLoading || publishersLoading || authorsLoading;

  // Configure Fuse.js for fuzzy search
  const booksFuse = useMemo(() => {
    return new Fuse(books, {
      keys: [
        { name: "title", weight: 2 },
        { name: "original_title", weight: 1.5 },
        { name: "author_name", weight: 1.5 },
        { name: "illustrator_name", weight: 1.5 },
        { name: "publisher_name", weight: 1 },
        { name: "book_series", weight: 1 },
        { name: "book_type", weight: 0.8 },
        { name: "isbn", weight: 1.5 },
        { name: "description", weight: 0.5 },
        { name: "age_group", weight: 0.8 },
      ],
      threshold: 0.4,
      includeScore: true,
      ignoreLocation: true,
      minMatchCharLength: 2,
    });
  }, [books]);

  const publishersFuse = useMemo(() => {
    return new Fuse(publishers, {
      keys: [
        { name: "name", weight: 2 },
        { name: "country", weight: 1 },
      ],
      threshold: 0.3,
      includeScore: true,
      ignoreLocation: true,
    });
  }, [publishers]);

  const authorsFuse = useMemo(() => {
    return new Fuse(authors, {
      keys: [{ name: "name", weight: 2 }],
      threshold: 0.3,
      includeScore: true,
      ignoreLocation: true,
    });
  }, [authors]);

  // Get unique book types and series from books
  const { bookTypes, bookSeries } = useMemo(() => {
    const types = [...new Set(books.map(b => b.book_type).filter(Boolean))] as string[];
    const series = [...new Set(books.map(b => b.book_series).filter(Boolean))] as string[];
    return { bookTypes: types, bookSeries: series };
  }, [books]);

  // Search results - Publishers first, then book types, then series, then authors, then books
  const searchResults = useMemo(() => {
    if (!debouncedQuery.trim() || debouncedQuery.length < 2) {
      return [];
    }

    const queryLower = debouncedQuery.toLowerCase();

    // Filter book types that match the query
    const bookTypeResults = bookTypes
      .filter(type => type.toLowerCase().includes(queryLower))
      .slice(0, 2)
      .map((type, index) => ({
        type: "book_type" as const,
        id: `type-${index}`,
        title: type,
        subtitle: "Book Type",
        slug: `/books?type=${encodeURIComponent(type)}`,
      }));

    // Filter series that match the query
    const seriesResults = bookSeries
      .filter(series => series.toLowerCase().includes(queryLower))
      .slice(0, 2)
      .map((series, index) => ({
        type: "series" as const,
        id: `series-${index}`,
        title: series,
        subtitle: "Series",
        slug: `/books?search=${encodeURIComponent(series)}`,
      }));

    const publisherResults = publishersFuse.search(debouncedQuery, { limit: 3 })
      .map((result) => ({
        type: "publisher" as const,
        id: result.item.id,
        title: result.item.name,
        subtitle: result.item.country,
        slug: `/publishers/${result.item.slug}`,
        logoUrl: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/logo/${result.item.slug}_logo.png`,
      }));

    const authorResults = authorsFuse.search(debouncedQuery, { limit: 3 })
      .map((result) => ({
        type: "author" as const,
        id: result.item.id,
        title: result.item.name,
        subtitle: undefined as string | undefined,
        slug: `/books?search=${encodeURIComponent(result.item.name)}`,
      }));

    const bookResults = booksFuse.search(debouncedQuery, { limit: 6 })
      .map((result) => ({
        type: "book" as const,
        id: result.item.id,
        title: result.item.title,
        subtitle: result.item.author_name || result.item.publisher_name,
        slug: result.item.slug,
        coverUrl: result.item.cover_url,
        metadata: [result.item.book_type, result.item.age_group].filter(Boolean).join(" • "),
      }));

    // Publishers first, then book types, then series, then authors, then books
    return [...publisherResults, ...bookTypeResults, ...seriesResults, ...authorResults, ...bookResults];
  }, [debouncedQuery, booksFuse, publishersFuse, authorsFuse, bookTypes, bookSeries]);

  // Combined items: history when no query, search results otherwise
  // Filter history to exclude archived publishers and books
  const displayItems = useMemo(() => {
    if (query.length < 2) {
      const filteredHistory = history.filter((item) => {
        // For publisher history items, check if the publisher still exists (not archived)
        if (item.type === "publisher" && item.slug) {
          const publisherSlug = item.slug.replace("/publishers/", "");
          return publishers.some((p) => p.slug === publisherSlug);
        }
        // For book history items, check if the book still exists (not archived)
        if (item.type === "book" && item.slug) {
          return books.some((b) => b.slug === item.slug);
        }
        return true;
      });
      return filteredHistory.slice(0, 5);
    }
    return searchResults;
  }, [query, history, searchResults]);

  const handleSelect = useCallback((result: SearchResult) => {
    // Add to history
    addToHistory({
      query: result.title,
      type: result.type,
      slug: result.slug,
    });
    
    setIsOpen(false);
    setQuery("");
    navigate(result.slug);
  }, [navigate, addToHistory]);

  const handleHistorySelect = useCallback((item: SearchHistoryItem) => {
    setIsOpen(false);
    setQuery("");
    if (item.slug) {
      navigate(item.slug);
    } else {
      navigate(`/books?search=${encodeURIComponent(item.query)}`);
    }
  }, [navigate]);

  const handleSearchSubmit = useCallback(() => {
    if (query.trim()) {
      addToHistory({
        query: query.trim(),
        type: "search",
      });
      navigate(`/books?search=${encodeURIComponent(query.trim())}`);
      setQuery("");
      setIsOpen(false);
    }
  }, [query, navigate, addToHistory]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const items = displayItems;
    
    if (!isOpen || items.length === 0) {
      if (e.key === "Enter" && query.trim()) {
        handleSearchSubmit();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, items.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && items[selectedIndex]) {
          const item = items[selectedIndex];
          if ('type' in item && 'id' in item) {
            handleSelect(item as SearchResult);
          } else {
            handleHistorySelect(item as SearchHistoryItem);
          }
        } else if (query.trim()) {
          handleSearchSubmit();
        }
        break;
      case "Escape":
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  const getIcon = (type: SearchResult["type"]) => {
    switch (type) {
      case "book": return <Book className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
      case "publisher": return <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
      case "author": return <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
      case "book_type": return <Layers className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
      case "series": return <Library className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
    }
  };

  const getTypeLabel = (type: SearchResult["type"] | SearchHistoryItem["type"]) => {
    switch (type) {
      case "book": return "Book";
      case "publisher": return "Publisher";
      case "author": return "Author";
      case "book_type": return "Type";
      case "series": return "Series";
      case "search": return "Search";
    }
  };

  // Mobile: just show search icon that opens modal
  if (isMobile) {
    return (
      <>
        <button
          onClick={() => setIsMobileOpen(true)}
          className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
          aria-label="Search"
        >
          <Search className="h-4 w-4 text-foreground" />
        </button>

        {/* Mobile Search Modal */}
        {isMobileOpen && (
          <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm animate-fade-in">
            <div className="flex flex-col h-full">
              <div className="flex items-center gap-2 p-4 border-b border-border">
                <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="Search books, authors..."
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSelectedIndex(-1);
                  }}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  className="flex-1 h-10 text-base border-0 bg-transparent focus-visible:ring-0 px-0"
                />
                <button
                  onClick={() => {
                    setIsMobileOpen(false);
                    setQuery("");
                  }}
                  className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : query.length < 2 && history.length > 0 ? (
                  // Show history
                  <div className="py-2">
                    <div className="flex items-center justify-between px-4 py-2">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Recent</span>
                      <button
                        onClick={clearHistory}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Clear all
                      </button>
                    </div>
                    {history.slice(0, 6).map((item, index) => (
                      <div
                        key={`${item.query}-${item.timestamp}`}
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors",
                          selectedIndex === index ? "bg-muted" : "active:bg-muted/50"
                        )}
                      >
                        <button
                          className="flex-1 flex items-center gap-3"
                          onClick={() => {
                            handleHistorySelect(item);
                            setIsMobileOpen(false);
                          }}
                        >
                          <div className="h-8 w-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <p className="font-medium truncate">{item.query}</p>
                          </div>
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                            {getTypeLabel(item.type)}
                          </span>
                        </button>
                        <button
                          onClick={() => removeFromHistory(item.query, item.type)}
                          className="p-1 hover:bg-muted rounded"
                        >
                          <X className="h-3 w-3 text-muted-foreground" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : query.length >= 2 && searchResults.length === 0 ? (
                  <div className="py-12 text-center px-4">
                    <p className="text-muted-foreground">
                      No results for "<span className="font-medium text-foreground">{debouncedQuery}</span>"
                    </p>
                  </div>
                ) : query.length >= 2 ? (
                  <div className="py-2">
                    {searchResults.map((result, index) => (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => {
                          handleSelect(result);
                          setIsMobileOpen(false);
                        }}
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors",
                          selectedIndex === index ? "bg-muted" : "active:bg-muted/50"
                        )}
                      >
                        {result.type === "book" && result.coverUrl ? (
                          <img src={result.coverUrl} alt="" className="h-12 w-8 object-cover rounded shadow-sm flex-shrink-0" />
                        ) : result.type === "publisher" && result.logoUrl ? (
                          <div className="h-10 w-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                            <img src={result.logoUrl} alt="" className="h-full w-full object-contain p-1" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                          </div>
                        ) : (
                          <div className="h-10 w-10 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                            {getIcon(result.type)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{result.title}</p>
                          {result.subtitle && <p className="text-sm text-muted-foreground truncate">{result.subtitle}</p>}
                        </div>
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">{getTypeLabel(result.type)}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center px-4">
                    <p className="text-muted-foreground">Type to search...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search books, authors..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setSelectedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-56 h-9 pl-9 pr-8 text-sm bg-background border-border focus:ring-primary/20"
        />
        {query ? (
          <button
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
          >
            <X className="h-3 w-3 text-muted-foreground" />
          </button>
        ) : null}
      </div>

      {/* Dropdown Results */}
      {isOpen && (query.length >= 2 || (query.length < 2 && history.length > 0)) && (
        <div className="absolute top-full mt-2 right-0 w-80 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50 animate-fade-in">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : query.length < 2 && history.length > 0 ? (
            // Show history
            <div className="max-h-[400px] overflow-y-auto">
              <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Recent searches</span>
                <button
                  onClick={clearHistory}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                >
                  <Trash2 className="h-3 w-3" />
                  Clear
                </button>
              </div>
              {history.slice(0, 5).map((item, index) => (
                <div
                  key={`${item.query}-${item.timestamp}`}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2.5 transition-colors",
                    selectedIndex === index ? "bg-muted" : "hover:bg-muted/50"
                  )}
                >
                  <button
                    className="flex-1 flex items-center gap-3"
                    onClick={() => handleHistorySelect(item)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="h-7 w-7 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <span className="text-sm font-medium truncate flex-1 text-left">{item.query}</span>
                    <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded flex-shrink-0">
                      {getTypeLabel(item.type)}
                    </span>
                  </button>
                  <button
                    onClick={() => removeFromHistory(item.query, item.type)}
                    className="p-1 hover:bg-muted rounded opacity-50 hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3 text-muted-foreground" />
                  </button>
                </div>
              ))}
            </div>
          ) : searchResults.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">
                No results for "<span className="font-medium text-foreground">{debouncedQuery}</span>"
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Try different keywords
              </p>
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              {searchResults.map((result, index) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleSelect(result)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors",
                    selectedIndex === index ? "bg-muted" : "hover:bg-muted/50"
                  )}
                >
                  {result.type === "book" && result.coverUrl ? (
                    <img 
                      src={result.coverUrl} 
                      alt="" 
                      className="h-10 w-7 object-cover rounded shadow-sm flex-shrink-0"
                    />
                  ) : result.type === "publisher" && result.logoUrl ? (
                    <div className="h-9 w-9 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img 
                        src={result.logoUrl} 
                        alt="" 
                        className="h-full w-full object-contain p-1"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).parentElement!.innerHTML = '<svg class="h-4 w-4 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="h-8 w-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                      {getIcon(result.type)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      <HighlightText text={result.title} query={debouncedQuery} />
                    </p>
                    {result.subtitle && (
                      <p className="text-xs text-muted-foreground truncate">
                        <HighlightText text={result.subtitle} query={debouncedQuery} />
                      </p>
                    )}
                  </div>
                  <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded flex-shrink-0">
                    {getTypeLabel(result.type)}
                  </span>
                </button>
              ))}
              
              {/* View all results link */}
              {searchResults.length > 0 && query.trim() && (
                <button
                  onClick={handleSearchSubmit}
                  className="w-full px-3 py-2.5 text-sm text-primary hover:bg-muted/50 transition-colors border-t border-border text-center"
                >
                  View all results for "{query.trim()}"
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchCommand;
